/* import __COLOCATED_TEMPLATE__ from './combined-assignment-limit-settings.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';

interface Args {
  model: any;
}

export default class CombinedAssignmentLimitSettings extends Component<Args> {
  @service declare intl: IntlService;
  @service declare notificationsService: any;

  get model() {
    return this.args.model;
  }

  get ticketsAssignmentDisabled() {
    return this.model.ticketsAssignmentDisabled;
  }

  get ticketAssignmentLimitEnabled() {
    return this.model.ticketAssignmentLimitEnabled;
  }

  get isValid() {
    return this.model.validations.isValid;
  }

  get areLimitsCombined() {
    return !this.ticketsAssignmentDisabled && !this.ticketAssignmentLimitEnabled;
  }

  get ticketsHaveSeparateLimit() {
    return !this.ticketsAssignmentDisabled && this.ticketAssignmentLimitEnabled;
  }

  @action async toggleTicketAssignmentDisabled() {
    this.model.ticketsAssignmentDisabled = !this.model.ticketsAssignmentDisabled;

    await this.save();
  }

  @action async toggleTicketAssignmentLimitEnabled() {
    this.model.ticketAssignmentLimitEnabled = !this.model.ticketAssignmentLimitEnabled;

    if (this.model.ticketAssignmentLimitEnabled && !this.model.ticketAssignmentLimit) {
      this.model.ticketAssignmentLimit = this.model.capacityLimit;
    }

    await this.save();
  }

  @action async validate() {
    this.model.errors.clear();

    let { validations } = await this.model.validations.validate();
    if (!validations.isValid) {
      // add error messages to the model so it's easier to display them in the UI
      validations.errors.forEach((error: any) => {
        this.model.errors.add(error.attribute, error.message);
      });
    }
  }

  @action async applyChanges(_event: Event) {
    await this.debouncedSave();
  }

  @action async debouncedSave(debounceTime = 800) {
    await this.validate();
    if (this.isValid) {
      debounce(this, this.save, debounceTime);
    }
  }

  @action async save() {
    await this.validate();
    if (!this.isValid) {
      return;
    }

    try {
      await this.model.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.workload-management.update-success-notification'),
      );
    } catch (_) {
      this.notificationsService.notifyError(
        this.intl.t('settings.workload-management.update-fail-notification'),
      );
      this.model.rollbackAttributes();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::WorkloadManagement::CombinedAssignmentLimitSettings': typeof CombinedAssignmentLimitSettings;
    'inbox/workload-management/combined-assignment-limit-settings': typeof CombinedAssignmentLimitSettings;
  }
}
